/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';
import { Coordinate } from 'ol/coordinate';
import { fromLonLat } from 'ol/proj';
import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import { SetLayerDisplayFeatures } from '../../state/active-map.actions';
import { MapService } from '../../services/map.service';
import { LoggerService } from '../../../shared/services/logger.service';
import { DashGisUtil } from '../../../shared/dash-gis-util';
import { FeatureService } from '../../services/feature.service';
import { Store } from '@ngxs/store';

@Injectable({
    providedIn: 'root'
})
export class ZoomLatLonService {

    readonly re = /^\(?\s*(-?\d+(?:\.\d*)?)\s*?[\s\\/,;|]?\s*(-?\d+(?:\.\d*)?)\s*\)?/;

    constructor(
        private logger: LoggerService,
        private mapService: MapService,
        private featureService: FeatureService,
        private store: Store,
    ) { }

    zoomToLatLon(longitude: number, latitude: number) {
        // convert to map coordinates
        // obviously, fromLonLat needs LatLon coords swapped
        const mapCoords = fromLonLat([
            longitude,
            latitude,
        ], this.mapService.projection);
        const feature = new Feature({
            geometry: new Point(mapCoords),
            name: 'Marker'
        });
        const featureId = `LatLon.${DashGisUtil.uid()}`;
        feature.setId(featureId);
        this.featureService.addFeature(feature);

        this.store.dispatch(new SetLayerDisplayFeatures({
            displayLayer: 'latlonzoom',
            featureIds: [ featureId ],
        }));

        this.mapService.map.getView().setCenter(mapCoords);
    }

    parseCoordinates(input: string): Coordinate {
        const matches = this.re.exec(input);
        if (!matches || matches.length !== 3) {
            return null;
        }
        const lat = parseFloat(matches[1]);
        const lon = parseFloat(matches[2]);
        if (lat > 90.0 || lat < -90.0 || lon > 180.0 || lon < -180.0) {
            return null;
        }
        return [ lat, lon ];
    }

    clearFeatures() {
        this.store.dispatch(new SetLayerDisplayFeatures({
            displayLayer: 'latlonzoom',
            featureIds: [ ],
        }));
    }
}
